<script setup>
defineProps({
  search: {
    type: String,
    default: '',
  },
  activeTag: {
    type: String,
    default: 'All',
  },
  activeSort: {
    type: String,
    default: 'latest',
  },
});

const emit = defineEmits(['update:search', 'update:activeTag', 'update:activeSort', 'reset']);

const popularTags = [
  { name: 'All', icon: '🍽️' },
  { name: 'Burger', icon: '🍔' },
  { name: 'Pizza', icon: '🍕' },
  { name: 'Biryani', icon: '🍛' },
  { name: 'Pasta', icon: '🍝' },
  { name: 'Coffee', icon: '☕' },
  { name: 'Dessert', icon: '🍰' },
  { name: 'Chinese', icon: '🥢' },
  { name: 'Fast Food', icon: '🍟' },
  { name: 'Buffet', icon: '🍱' },
  { name: 'Steak', icon: '🥩' },
  { name: 'Kebab', icon: '🍢' },
  { name: 'Cafe', icon: '🧋' },
];

const sortOptions = [
  { value: 'latest', label: '🔥 Latest Reviews' },
  { value: 'most_popular', label: '⭐ Most Popular' },
  { value: 'top_reacted', label: '❤️ Top Reacted' },
];

const handleSearchInput = (e) => {
  emit('update:search', e.target.value);
};

const handleTagClick = (tag) => {
  emit('update:activeTag', tag);
};

const handleSortChange = (e) => {
  emit('update:activeSort', e.target.value);
};
</script>

<template>
  <div class="bg-base-100 p-3 sm:p-4 rounded-2xl sm:rounded-3xl border border-base-200/80 shadow-sm space-y-3">
    <!-- Top Search & Sort Row -->
    <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
      <!-- Search Input -->
      <div class="relative flex-1">
        <div class="absolute left-3.5 top-1/2 -translate-y-1/2 text-base-content/40 pointer-events-none">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4.5 w-4.5 sm:h-5 sm:w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input
          :value="search"
          type="text"
          placeholder="Search food, restaurants, or tags..."
          class="input input-bordered input-sm sm:input-md w-full rounded-xl sm:rounded-2xl pl-10 pr-9 text-xs sm:text-sm focus:ring-2 ring-primary/20 bg-base-200/40 border-base-200 focus:bg-base-100 transition-all"
          @input="handleSearchInput"
        >
        <button
          v-if="search"
          type="button"
          class="btn btn-ghost btn-circle btn-xs absolute right-2.5 top-1/2 -translate-y-1/2 text-base-content/50 hover:text-base-content"
          title="Clear search"
          @click="emit('update:search', '')"
        >
          ✕
        </button>
      </div>

      <!-- Sort Dropdown -->
      <div class="shrink-0">
        <select
          :value="activeSort"
          class="select select-bordered select-sm sm:select-md w-full sm:w-auto rounded-xl sm:rounded-2xl bg-base-200/40 border-base-200 text-xs sm:text-sm font-medium focus:ring-2 ring-primary/20 focus:bg-base-100"
          @change="handleSortChange"
        >
          <option
            v-for="opt in sortOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
      </div>
    </div>

    <!-- Tags Scrollable Carousel -->
    <div class="relative">
      <div class="flex items-center gap-1.5 sm:gap-2 overflow-x-auto pb-1 no-scrollbar text-xs sm:text-sm scroll-smooth">
        <button
          v-for="tag in popularTags"
          :key="tag.name"
          type="button"
          class="inline-flex items-center gap-1.5 py-1.5 sm:py-2 px-3 sm:px-3.5 rounded-xl font-medium cursor-pointer transition-all duration-200 shrink-0 select-none border"
          :class="activeTag === tag.name 
            ? 'bg-primary text-primary-content border-primary shadow-sm scale-102' 
            : 'bg-base-200/60 hover:bg-base-200 border-transparent text-base-content/70 hover:text-base-content'"
          @click="handleTagClick(tag.name)"
        >
          <span>{{ tag.icon }}</span>
          <span>{{ tag.name === 'All' ? 'All Reviews' : tag.name }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
